/* eslint-disable no-console */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch } from "react-redux";
import axios from 'axios';
import { Modal, Button } from 'antd';
import { useSSE } from "./utility/sse";
import { Constants } from "./config/constants";
import { AwsConfig } from "./aws-config";
import { Auth } from "./services/authService";
import {
    fetchClassTeacherZoomEmails,
    fetchOfficialTeacherEmails, fetchStudentEmails,
    fetchTeacherUnifiedEmails
} from "./redux/domain-emails/actionCreator";
import {
    fetchClassTeacherZoomUnseenEmails, fetchStudentUnseenEmails,
    fetchTeacherUnseenEmails,
    fetchUnseenOfficialEmails
} from "./redux/notifications/actionCreator";

// function urlBase64ToUint8Array(base64String) {
//     if (!base64String) return new Uint8Array();
//     const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
//     const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
//     const rawData = window.atob(base64);
//     const outputArray = new Uint8Array(rawData.length);
//     for (let i = 0; i < rawData.length; ++i) {
//         outputArray[i] = rawData.charCodeAt(i);
//     }
//     return outputArray;
// }

// 1. Also fix the padding calculation typo in urlBase64ToUint8Array:
function urlBase64ToUint8Array(base64String) {
    if (!base64String) return new Uint8Array();
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export const subscribeUserManual = async (userId) => {
    const token = Auth?.getAccessToken();
    if (!token || !userId) return null;

    if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
            const registration = await navigator.serviceWorker.ready;
            const permission = await Notification.requestPermission();

            if (permission === 'granted') {
                const subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(AwsConfig.VAPID_PUBLIC_KEY)
                });

                const response = await axios.post(`${Constants.SSE}save-subscription`,
                    { subscription, userId: userId },
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });

                localStorage.setItem('notifications_enabled', 'true');
                return response.data;
            }
            return null;
        } catch (error) {
            console.error("Manual subscription failed:", error);
            return null;
        }
    }
};

export const unsubscribeUserManual = async (userId) => {
    const token = Auth?.getAccessToken();
    if (!token || !userId) return;

    if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
                await subscription.unsubscribe();
                await axios.post(`${Constants.SSE}remove-subscription`,
                    { endpoint: subscription.endpoint, userId },
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
            }

            localStorage.removeItem('notifications_enabled');
            console.log("🚫 Device unlinked and unsubscribed.");
            return true;
        } catch (error) {
            console.error("Unsubscribe failed:", error);
            return false;
        }
    }
};

export const AppSSEProvider = ({ children }) => {
    const dispatch = useDispatch();
    const userInfo = Auth.getUserInfo();
    const userId = userInfo?._id;
    const token = Auth?.getAccessToken();

    const [isPromptModalVisible, setIsPromptModalVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 1. Memoize SSE Endpoint URL to guarantee a fresh token
    const sseUrl = useMemo(() => {
        return token ? `${Constants.SSE}teacher-events?token=${token}` : null;
    }, [token]);

    // 2. Add the "new-zoom-email" switch case in handleSseMessage:
    const handleSseMessage = useCallback((data) => {
        switch (data.type) {
            case "new-email":
                if (Notification.permission === "granted") {
                    new Notification("HURDCO Email", { body: data.subject || "New email received" });
                }
                dispatch(fetchTeacherUnifiedEmails({ page: 1, perPage: 20, view: "inbox" }));
                dispatch(fetchTeacherUnseenEmails());
                break;

            case "new-zoom-email":
                if (Notification.permission === "granted") {
                    new Notification("📹 Teacher Zoom Email", { body: data.subject || "New Class Teacher Zoom email received" });
                }
                // Dispatch action to refresh Zoom email list in Redux
                dispatch(fetchClassTeacherZoomEmails({ page: 1, perPage: 20 }));
                dispatch(fetchClassTeacherZoomUnseenEmails());
                break;

            case "new-class-teacher-student-email":
                if (Notification.permission === "granted") {
                    new Notification(`🎓 Email for ${data.studentName || 'Student'}`, {
                        body: `From: ${data.from}\nSubject: ${data.subject || 'New student email'}`
                    });
                }
                // Refresh student email list action in Redux
                dispatch(fetchStudentEmails({ page: 1, perPage: 20 }));
                dispatch(fetchStudentUnseenEmails());
                break;

            case "new-official-email":
                dispatch(fetchOfficialTeacherEmails({ page: 1, perPage: 20, view: "inbox" }));
                dispatch(fetchUnseenOfficialEmails());
                break;

            default:
                break;
        }
    }, [dispatch]);

    // 3. Connect SSE Stream
    useSSE(sseUrl, handleSseMessage);

    // 4. Background Push Subscription Logic
    useEffect(() => {
        let modalTimer;

        const handleSessionSecurityCheck = async () => {
            const currentToken = Auth?.getAccessToken();
            if (!currentToken || !userId) return;

            if ('serviceWorker' in navigator && 'PushManager' in window) {
                try {
                    const registration = await navigator.serviceWorker.ready;
                    let subscription = await registration.pushManager.getSubscription();
                    const justLoggedIn = sessionStorage.getItem('just_logged_in') === 'true';

                    if (subscription) {
                        if (justLoggedIn) sessionStorage.removeItem('just_logged_in');

                        await axios.post(`${Constants.SSE}save-subscription`,
                            { subscription, userId: userId },
                            { headers: { 'Authorization': `Bearer ${currentToken}` } }
                        );
                        console.log("🔄 Active device verified. Subscription synchronized silently.");
                        return;
                    }

                    if (!subscription && Notification.permission === 'granted') {
                        console.log("⚠️ Push subscription lost after build. Resubscribing...");
                        const resubscribed = await subscribeUserManual(userId);
                        if (resubscribed) {
                            console.log("✅ Device silently resubscribed!");
                            return;
                        }
                    }

                    if (justLoggedIn && Notification.permission === 'default') {
                        sessionStorage.removeItem('just_logged_in');

                        modalTimer = setTimeout(() => {
                            setIsPromptModalVisible(true);
                        }, 4000);
                    }

                } catch (error) {
                    console.error("Background subscription verification failed:", error);
                }
            }
        };

        handleSessionSecurityCheck();

        return () => {
            if (modalTimer) clearTimeout(modalTimer);
        };
    }, [userId]);

    const handleModalAccept = async () => {
        setIsSubmitting(true);
        const result = await subscribeUserManual(userId);
        setIsSubmitting(false);
        setIsPromptModalVisible(false);

        if (result) {
            window.location.reload();
        }
    };

    return (
        <>
            {children}

            <Modal
                title="🔊 Stay Updated instantly!"
                visible={isPromptModalVisible}
                onCancel={() => setIsPromptModalVisible(false)}
                footer={[
                    <Button key="back" onClick={() => setIsPromptModalVisible(false)}>
                        Not Now
                    </Button>,
                    <Button key="submit" type="primary" loading={isSubmitting} onClick={handleModalAccept}>
                        Yes, Enable Notifications
                    </Button>,
                ]}
            >
                <p>Would you like to receive instant push notifications on this device whenever you receive new official emails or portal updates?</p>
                <small style={{ color: '#8c8c8c' }}>You can change this preference anytime using the bell icon in the top header menu.</small>
            </Modal>
        </>
    );
};